import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import {
  fetchCollectionById,
  deleteCollection,
  removeBookFromCollection,
} from "@/api/collections";
import AdminLayout from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft,
  Edit,
  Trash,
  BookMarked,
  Book,
  BookOpen,
  Star,
  Eye,
  ChevronUp,
  ChevronDown,
  Calendar,
  CalendarCheck,
  Check,
  X,
  ImageIcon,
  Plus,
  EyeOff,
} from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { format } from "date-fns";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/components/ui/use-toast";

// Get a color for collection based on name
const getCollectionColor = (collectionTitle: string): string => {
  // Generate a consistent color based on the collection title
  const colors = [
    "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300",
    "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300",
    "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300",
    "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300",
    "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
    "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300",
    "bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-300",
    "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300",
    "bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300",
    "bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300",
  ];

  // Create a hash of the collection title to pick a consistent color
  let hash = 0;
  for (let i = 0; i < collectionTitle.length; i++) {
    hash = collectionTitle.charCodeAt(i) + ((hash << 5) - hash);
  }
  hash = Math.abs(hash);

  return colors[hash % colors.length];
};

const CollectionDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("details");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Convert id to number
  const collectionId = id ? parseInt(id, 10) : 0;

  // Fetch collection data
  const { data: collection, isLoading } = useQuery({
    queryKey: ["collection", collectionId],
    queryFn: () => fetchCollectionById(collectionId),
    enabled: !!collectionId,
  });

  // Delete collection mutation
  const deleteMutation = useMutation({
    mutationFn: deleteCollection,
    onSuccess: () => {
      navigate("/admin/collections");
      toast({
        title: "Collection deleted",
        description: "The collection has been permanently deleted.",
      });
    },
  });

  // Remove book from collection mutation
  const removeBookMutation = useMutation({
    mutationFn: ({
      collectionId,
      bookId,
    }: {
      collectionId: number;
      bookId: number;
    }) => removeBookFromCollection(collectionId, bookId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["collection", collectionId] });
      toast({
        title: "Book removed",
        description: "The book has been removed from this collection.",
      });
    },
  });

  // Handle collection deletion
  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync(collectionId);
    } catch (error) {
      console.error("Failed to delete collection:", error);
      toast({
        title: "Error",
        description: "Failed to delete collection. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Handle removing a book from collection
  const handleRemoveBook = async (bookId: number) => {
    try {
      await removeBookMutation.mutateAsync({ collectionId, bookId });
    } catch (error) {
      console.error("Failed to remove book from collection:", error);
      toast({
        title: "Error",
        description: "Failed to remove book from collection. Please try again.",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return (
      <AdminLayout title="Collection Details">
        <div className="flex items-center justify-center h-full">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      </AdminLayout>
    );
  }

  if (!collection) {
    return (
      <AdminLayout title="Collection Details">
        <div className="flex flex-col items-center justify-center h-full gap-4">
          <h1 className="text-2xl font-bold">Collection not found</h1>
          <p className="text-muted-foreground">
            The collection you're looking for doesn't exist or has been removed.
          </p>
          <Button onClick={() => navigate("/admin/collections")}>
            Go back to Collections
          </Button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Collection Details">
      {/* Header with back button, title and actions */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">{collection.title}</h1>
            {collection.subtitle && (
              <p className="text-sm text-muted-foreground">
                {collection.subtitle}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="default"
            onClick={() => navigate(`/admin/collections/${collection.id}/edit`)}
          >
            <Edit className="mr-2 h-4 w-4" /> Edit Collection
          </Button>

          <AlertDialog
            open={deleteDialogOpen}
            onOpenChange={setDeleteDialogOpen}
          >
            <AlertDialogTrigger asChild>
              <Button variant="destructive">
                <Trash className="mr-2 h-4 w-4" />
                Delete
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently delete the collection "
                  {collection.title}". Books in this collection will not be
                  deleted, but they will no longer be part of this collection.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleDelete}>
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        <div className="md:col-span-2 space-y-6">
          {/* Collection Info Card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle>Collection Details</CardTitle>
              {collection.subtitle && (
                <CardDescription>{collection.subtitle}</CardDescription>
              )}
            </CardHeader>
            <CardContent className="grid gap-6 md:grid-cols-2">
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">
                  Title
                </p>
                <p className="text-lg font-medium">{collection.title}</p>
              </div>

              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">
                  Books
                </p>
                <p className="flex items-center gap-1">
                  <Book className="h-4 w-4 text-muted-foreground" />
                  <span className="text-lg font-medium">
                    {collection.books.length} book
                    {collection.books.length !== 1 ? "s" : ""}
                  </span>
                </p>
              </div>

              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">
                  Position
                </p>
                <p className="text-lg font-medium">{collection.position}</p>
              </div>

              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">
                  Created
                </p>
                <p className="text-base flex items-center gap-1">
                  <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                  {format(new Date(collection.createdAt), "MMM d, yyyy")}
                </p>
              </div>

              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">
                  Last Updated
                </p>
                <p className="text-base flex items-center gap-1">
                  <CalendarCheck className="h-3.5 w-3.5 text-muted-foreground" />
                  {format(new Date(collection.updatedAt), "MMM d, yyyy")}
                </p>
              </div>

              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">
                  Status
                </p>
                <Badge
                  variant={collection.isActive ? "default" : "outline"}
                  className={cn(
                    "px-2 py-1",
                    collection.isActive
                      ? "bg-green-100 text-green-800 hover:bg-green-200 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:hover:bg-green-900/40"
                      : "bg-gray-100 text-gray-800 hover:bg-gray-200 dark:bg-gray-800/50 dark:text-gray-300 dark:hover:bg-gray-800/70"
                  )}
                >
                  {collection.isActive ? (
                    <Check className="mr-1 h-3 w-3 inline" />
                  ) : (
                    <X className="mr-1 h-3 w-3 inline" />
                  )}
                  {collection.isActive ? "Active" : "Inactive"}
                </Badge>
              </div>

              {collection.description && (
                <div className="md:col-span-2">
                  <p className="text-sm font-medium text-muted-foreground mb-1">
                    Description
                  </p>
                  <p className="text-base whitespace-pre-line p-3 bg-secondary/30 rounded-md">
                    {collection.description}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Books Card */}
          <Card>
            <CardHeader>
              <CardTitle>Books in this Collection</CardTitle>
              <CardDescription>
                {collection.books.length} book
                {collection.books.length !== 1 ? "s" : ""} in this collection
              </CardDescription>
            </CardHeader>
            <CardContent>
              {collection.books.length === 0 ? (
                <div className="text-center py-8">
                  <BookOpen className="h-12 w-12 mx-auto text-muted-foreground opacity-50 mb-3" />
                  <h3 className="text-lg font-medium mb-1">
                    No books in this collection
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    This collection doesn't contain any books yet.
                  </p>
                  <Button
                    onClick={() =>
                      navigate(`/admin/collections/${collection.id}/edit`)
                    }
                    className="gap-2"
                  >
                    <Plus className="h-4 w-4" />
                    Add Books
                  </Button>
                </div>
              ) : (
                <div className="border rounded-md overflow-hidden">
                  <Table>
                    <TableHeader className="bg-muted/50">
                      <TableRow>
                        <TableHead>Book</TableHead>
                        <TableHead>Position</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {collection.books
                        .sort((a, b) => a.position - b.position)
                        .map((book) => (
                          <TableRow key={book.id} className="hover:bg-muted/30">
                            <TableCell>
                              <div className="flex items-center gap-3">
                                {book.coverImage ? (
                                  <img
                                    src={book.coverImage}
                                    alt={book.title}
                                    className="w-10 h-10 object-cover rounded-md"
                                  />
                                ) : (
                                  <div className="w-10 h-10 flex items-center justify-center bg-secondary rounded-md">
                                    <BookOpen className="w-5 h-5 text-secondary-foreground opacity-70" />
                                  </div>
                                )}
                                <div className="flex flex-col min-w-0">
                                  <Button
                                    variant="link"
                                    className="h-auto p-0 font-semibold text-left justify-start"
                                    onClick={() =>
                                      navigate(`/admin/books/${book.id}`)
                                    }
                                  >
                                    <span className="truncate">
                                      {book.title}
                                    </span>
                                  </Button>
                                  <div className="flex items-center gap-2">
                                    {book.isActive === false && (
                                      <Badge
                                        variant="outline"
                                        className="bg-gray-100 text-gray-600 dark:bg-gray-800/50 dark:text-gray-400 h-5 px-1 mt-1"
                                      >
                                        <EyeOff className="h-3 w-3 mr-1" />
                                        Unpublished
                                      </Badge>
                                    )}
                                    {book.authors &&
                                      book.authors.length > 0 && (
                                        <span className="text-xs text-muted-foreground truncate">
                                          {book.authors
                                            .map((author) => author.name)
                                            .join(", ")}
                                        </span>
                                      )}
                                  </div>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant="outline"
                                className="bg-background"
                              >
                                {book.position + 1}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-2">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={() =>
                                    navigate(`/admin/books/${book.id}`)
                                  }
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8 text-destructive hover:text-destructive"
                                    >
                                      <Trash className="h-4 w-4" />
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>
                                        Remove book from collection?
                                      </AlertDialogTitle>
                                      <AlertDialogDescription>
                                        This will remove "{book.title}" from
                                        this collection. The book itself will
                                        not be deleted.
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>
                                        Cancel
                                      </AlertDialogCancel>
                                      <AlertDialogAction
                                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                        onClick={() =>
                                          handleRemoveBook(book.id)
                                        }
                                      >
                                        Remove
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {collection.books.length > 0 && (
                <div className="flex justify-end mt-4">
                  <Button
                    variant="outline"
                    onClick={() =>
                      navigate(`/admin/collections/${collection.id}/edit`)
                    }
                    className="gap-2"
                  >
                    <Edit className="h-4 w-4" />
                    Manage Books
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          {/* Cover Image Card */}
          <Card>
            <CardHeader>
              <CardTitle>Cover Image</CardTitle>
            </CardHeader>
            <CardContent>
              {collection.coverImage ? (
                <div className="rounded-md overflow-hidden border flex items-center justify-center p-4">
                  <img
                    src={collection.coverImage}
                    alt={collection.title}
                    className="max-w-full max-h-60 object-contain"
                  />
                </div>
              ) : (
                <div
                  className={cn(
                    "h-40 flex items-center justify-center rounded-md border",
                    getCollectionColor(collection.title)
                  )}
                >
                  <BookMarked className="h-12 w-12 opacity-70" />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Collection Stats Card */}
          <Card>
            <CardHeader>
              <CardTitle>Collection Stats</CardTitle>
              <CardDescription>Collection metrics</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Books</span>
                <div className="flex items-center gap-1">
                  <BookOpen className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">{collection.books.length}</span>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Status</span>
                <div className="flex items-center gap-1">
                  {collection.isActive ? (
                    <Check className="h-4 w-4 text-green-500" />
                  ) : (
                    <X className="h-4 w-4 text-gray-500" />
                  )}
                  <span className="font-medium">
                    {collection.isActive ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Position</span>
                <div className="flex items-center gap-1">
                  <span className="font-medium">{collection.position}</span>
                </div>
              </div>

              <div className="pt-2 mt-2 border-t border-border text-xs text-center text-muted-foreground">
                You can modify all collection properties using the Edit
                Collection button.
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
};

export default CollectionDetails;
